import {UpsellDialogEntryPoint} from '@Libraries/upsell-dialog.library';
import type {PremiumOnlyFeatureDialogOptions} from '@Libraries/upsell-dialog.library';
import type {EventCalendarMetaData, EventHashedID, EventsFilters, EventsListLazyData, EventsReduxState, EventStatusFilterMap, EventVO} from '@Components/events/events.types';
import {EventSortBy, EventsSharedWithMeFilter, EventStatus, EventsViewType, EventStatusFilter} from '@Components/events/events.types';
import {appendFontStyleSheetToDOM, getFontStyleSheetPath} from '@Libraries/font-library';
import {getFontFamilyStyleString} from '@Utils/font.util';
import {
  areDatesEqual,
  convertUTCTimeToGivenTimezone,
  getDateFromUnixTimestamp,
  getReadableShortMonth,
  getUnixTimestamp,
  getYearMonthDateFormat,
  hasDatedPassed,
  isBetweenDates,
  isFutureDate,
} from '@Utils/date.util';
import {LoadingStates} from '@Utils/loading.util';
import {getUniqueString} from '@Utils/string.util';
import {openEventPreviewModal} from '@Modals/event-preview-modal/event-preview-modal.library';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {ALL_WEEKDAYS, Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {initSocialMediaProfileLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {getUserId, isUserLoggedIn} from '@Libraries/user.library';
import {isPAYGUser} from '@Utils/user.util';
import type {EventTypeAiSearchParams, MediaTypes} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import {hideLoading, showLoading} from '@Libraries/loading-toast-library';
import {onAjaxError} from '@Utils/ajax.util';
import {noop} from '@Utils/general.util';
import {getTranslatedWeekdays} from '@Components/event-wizard/event-wizard-date-time-summary/event-wizard-date-time-summary.types';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import {redirectUser} from '@Utils/browser.util';
import {cleanURIComponent, isSafeWebUrl} from '@Utils/url.util';
import {MY_STUFF_HASHES} from '@Libraries/my-stuff-library';
import {getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem} from '@Utils/session-storage.util';
import type {TimeZone} from 'timezones-list';
import timezones from 'timezones-list';
import {getGMTOffsetMinutesFromTimeZone} from '@Components/event-wizard/timezone-content/timezone-content.helper';
import {openUpsellingDialogForEmailCampaigns} from '@Libraries/email-campaigns-library';
import {repoURL} from '@Libraries/s3-library';
import {GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import {getWriteBucket} from '@Utils/s3.util';
import type {EventsIframeModalProps} from '@Modals/event-preview-modal/event-preview-modal.types';
import {openDeleteEventConfirmationModal} from '@Modals/delete-event-confirmation-modal/delete-event-confirmation-modal.library';
import {EventsCreateGA4Source, EventUpsellGA4Source, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {assertNever} from '@Utils/assert-never.util';
import {PromotionType} from '@Components/mystuff-event-promotion-page/mystuff-event-promotion-page.types';
import type {DeleteEventConfirmationPanelProps} from '@Panels/delete-event-confirmation-panel/delete-event-confirmation-panel.types';
import type {EventPendingEmailInvitation} from '@Panels/manage-event-collaborators-panel/manage-event-collaborators-panel.types';
import {getColorPaletteForContainer, PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import {openTeamsUpsellDialog, TeamsUpsellSource} from '@Libraries/teams-upsell.library';

export const EVENTS_LAZY_BATCH_SIZE = 50;

export const EVENT_WIZARD_SOURCE_URL_PARAM = 'st';
export const EVENT_PAGE_SOURCE_URL_PARAM = 'st';
/**
 * Marks a request as coming from the owner's draft preview rather than from the bare event URL. A draft's
 * page refuses to render without it, so it has no address that works on its own and cannot be mistaken for a
 * published page. Mirrored in PHP as `E::DRAFT_PREVIEW_URL_PARAM`; the two must stay in step.
 */
export const EVENT_DRAFT_PREVIEW_URL_PARAM = 'draftPreview';
export const EVENT_PAGE_CONFETTI_URL_PARAM = 'sc';

export const DEFAULT_EVENTS_STYLE_FONT_FAMILY = 'Figtree';

export const EVENT_REGISTRATIONS_LIMIT_PAYG = 100;

// The events ad, shown in the empty state's artwork slot in place of the still illustration.
export const EVENTS_PROMO_VIDEO_URL = 'https://www.youtube.com/watch?v=qygxyCsxm_Q';

export const EMBED_EVENT_HELP_CENTRE_ARTICLE_LINK =
  'https://support.postermywall.com/hc/en-us/articles/36928038136333-How-to-embed-your-PosterMyWall-events-schedule-on-your-website';

export const FEEDBACK_LINK = 'https://surveys.hotjar.com/fcc93b4b-cefe-4d68-889a-31900727e9dc';

// Must match Premium::PREMIUM_ONLY_FEATURE_NAME_EVENT_TICKETING (backend) and the core.js mirror.
// Passed as a literal rather than via window.PMW: that global ships in the separate legacy core.js
// bundle, so a stale legacy build would silently send '' and render the default (teams) dialog.
const PREMIUM_ONLY_FEATURE_NAME_EVENT_TICKETING = 'eventticketing';
// Same constraint as PREMIUM_ONLY_FEATURE_NAME_EVENT_TICKETING above: must match
// Premium::PREMIUM_ONLY_FEATURE_NAME_EVENT_CUSTOM_FORMS (backend) and the core.js mirror, and is
// passed as a literal rather than via window.PMW because that global ships in the separate legacy
// core.js bundle and would be undefined until that bundle is rebuilt.
const PREMIUM_ONLY_FEATURE_NAME_EVENT_CUSTOM_FORMS = 'eventcustomforms';
const TICKETING_UPSELL_LOADING_KEY = 'ticketingUpsellDialog';

export const TICKET_CHECKIN_SURVEY_HOTJAR_ID = 'event-ticketing-feedback-hotjar';
export const TICKET_CHECKIN_SURVEY_URI_SHOW = 'event/shouldShowTicketCheckinSurvey';
export const TICKET_CHECKIN_SURVEY_URI_MARK_COMPLETE = 'event/markTicketCheckinSurveyComplete';

export const EVENT_CALENDAR_EMBED_WORDPRESS_PLUGIN_URL = 'https://wordpress.org/plugins/postermywall-events-calendar/';

export interface DeleteEventFlowParams {
  eventHashedId: EventHashedID;
  onDeleteSuccess: VoidFunction;
  doShowDeleteButtonLoader?: boolean;
}

export enum EventPagePreviewType {
  DESKTOP = 'desktop',
  MOBILE = 'mobile',
}

export enum EventUpsellType {
  MORE_EVENTS,
  MORE_EVENTS_PREMIUM,
  REMOVE_WATERMARK,
  DESCRIPTION_REWRITE_AI,
  REGISTRATION_OPTIONS,
  RECURRING,
  NUM_GUESTS_LIMIT,
  VIEW_ANALYTICS,
  REMINDERS,
  TICKETING,
  CUSTOM_FORMS,
  SHARE_TEAM,
  EVENT_ONE_CLICK_PROMOTION,
}

export enum EventPromoteManageGA4Source {
  MYSTUFF = 'MyStuff',
  MYSTUFF_POPUP = 'MyStuff_popup',
  PROMOTE_PAGE = 'PromotePage',
}

/**
 * query params appended to the wizard url
 * for example, for prefilling start date, we use query param 'sd'
 * ENSURE THIS IS THE SAME AS IN PHP SIDE. SEE "EventWizardURLMaker.php"
 */
export enum EventWizardPrefillValueQueryParam {
  START_DATE = 'sd',
  DESIGN_TEMPLATE_HID = 'tcph',
  DESIGN_TEMPLATE_SOURCE = 'tcps',
  DESIGN_PAGE_ID = 'dpid',
  GALLERY_CATEGORY_URI = 'gc',
}

export type EventWizardPrefillParams = Partial<Record<EventWizardPrefillValueQueryParam, string>>;
export type EventWizardQueryParams = Record<string, string>;

export enum EventWizardPrefillSource {
  DEFAULT,
  DUPLICATE,
  UPDATE,
  PREFILL_FROM_DESIGN,
  PREFILL_FROM_START_DATE_SELECTION,
  PREFILL_FROM_GALLERY_CATEGORY,
}

export enum EventPageSource {
  DEFAULT,
  FROM_WIZARD_COMPLETION = 1,
  FOR_WIZARD_IFRAME = 2,
}

/**
 * starting the calendar from monday onwards for events specifically
 */
export const getTranslatedPluralWeekdaysForEvents = (): string[] => {
  return [
    window.i18next.t('pmwjs_calendar_mondays'),
    window.i18next.t('pmwjs_calendar_tuesdays'),
    window.i18next.t('pmwjs_calendar_wednesdays'),
    window.i18next.t('pmwjs_calendar_thursdays'),
    window.i18next.t('pmwjs_calendar_fridays'),
    window.i18next.t('pmwjs_calendar_saturdays'),
    window.i18next.t('pmwjs_calendar_sundays'),
  ];
};

export const isEventsFeatureEnabled = (): boolean => {
  return !!window.reactPageProps.isEventsFeatureEnabled;
};

export const isEventPromotionEnabled = (): boolean => {
  return !!window.reactPageProps.isEventPromotionEnabled;
};

export const getEventWizardURLWithPrefillSource = (prefillSource: EventWizardPrefillSource, prefillParamValue: EventWizardPrefillParams = {}, eventHashedId = ''): string => {
  const baseURI = eventHashedId ? `event/wizard/${eventHashedId}` : 'event/wizard';
  const url = new URL(window.PMW.util.site_url(baseURI));

  Object.entries(getEventWizardQueryParams(prefillSource, prefillParamValue)).forEach(([param, value]) => {
    url.searchParams.append(param, value);
  });
  return url.toString();
};

/** The wizard's query params as its page URL carries them: the source (`st`) and that source's prefill values. */
export const getEventWizardQueryParams = (prefillSource: EventWizardPrefillSource, prefillParamValue: EventWizardPrefillParams = {}): EventWizardQueryParams => {
  const queryParams: EventWizardQueryParams = {[EVENT_WIZARD_SOURCE_URL_PARAM]: prefillSource.toString()};

  Object.entries(prefillParamValue).forEach(([param, value]) => {
    if (value !== undefined) {
      queryParams[param] = value;
    }
  });
  return queryParams;
};

export const getURLToEditEvent = (event: EventVO): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.UPDATE, {}, event.hashedID);
};

export const getURLToDuplicateEvent = (event: EventVO): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.DUPLICATE, {}, event.hashedID);
};

export const getPromotePageURL = (event: EventVO): string => {
  return window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.promoteEvent}/${event.hashedID}`);
};

/**
 * The wizard and My Stuff are two separate page loads, so the "your event was created" confirmation cannot be a
 * growl raised where the save happened — the redirect would wipe it before it was read. The wizard leaves this
 * one-shot marker behind instead, and whichever My Stuff page the organiser lands on picks it up.
 *
 * Session storage rather than a query param: the destination is a hash route, and a param would survive in the
 * URL the organiser then bookmarks or shares.
 */
const EVENT_CREATED_SESSION_STORAGE_KEY = 'eventCreatedInWizard';

export const markEventAsJustCreated = (): void => {
  setSessionStorageItem(EVENT_CREATED_SESSION_STORAGE_KEY, true);
};

/** Reads the marker and clears it in the same breath, so the confirmation shows once and never again. */
export const wasEventJustCreated = (): boolean => {
  const wasJustCreated = getSessionStorageItem(EVENT_CREATED_SESSION_STORAGE_KEY) === true;
  removeSessionStorageItem(EVENT_CREATED_SESSION_STORAGE_KEY);
  return wasJustCreated;
};

export const startEventPromotionFlow = (event: EventVO, source: EventPromoteManageGA4Source): void => {
  if (isEventPromotionEnabled()) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_CLICK, {[GA4EventParamName.SOURCE]: source.toString()});
    window.location.href = getPromotePageURL(event);
  }
};

export const startEventManageFlow = (event: EventVO, source: EventPromoteManageGA4Source): void => {
  if (isEventPromotionEnabled()) {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_MANAGE_CLICK, {[GA4EventParamName.SOURCE]: source.toString()});
    window.location.href = window.PMW.util.site_url(`posters/mine${MY_STUFF_HASHES.manageEvent}/${event.hashedID}`);
  }
};

export const getEventWizardURLWithDesignPrefill = (designHID: string, designSource: MediaTypes, pageId?: string): string => {
  const params: Record<string, string> = {
    [EventWizardPrefillValueQueryParam.DESIGN_TEMPLATE_HID.toString()]: designHID,
    [EventWizardPrefillValueQueryParam.DESIGN_TEMPLATE_SOURCE.toString()]: designSource.toString(),
  };

  if (pageId) {
    params[EventWizardPrefillValueQueryParam.DESIGN_PAGE_ID.toString()] = pageId;
  }

  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.PREFILL_FROM_DESIGN, params);
};

export const getEventWizardURLWithDatePrefill = (date: Date): string => {
  return getEventWizardURLWithPrefillSource(EventWizardPrefillSource.PREFILL_FROM_START_DATE_SELECTION, {
    [EventWizardPrefillValueQueryParam.START_DATE.toString()]: getYearMonthDateFormat(date),
  });
};

export const openEventWizardWithStartDatePrefillFromContentPlanner = (date: Date): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_CREATE_CLICK, {[GA4EventParamName.SOURCE]: 'Content planner'});
  openEventWizardWithStartDatePrefill(date);
};

export const openEventWizardWithStartDatePrefill = (date: Date): void => {
  window.location.href = getEventWizardURLWithDatePrefill(date);
};

export const getEventPageURL = (event: EventVO): string => {
  return window.PMW.util.site_url(getBaseEventPageURL(event));
};

export const getEventsCalendarPageURL = (hashedId: string): string => {
  return window.PMW.util.site_url(`e/calendar/${hashedId}`);
};

export const getEventPageURLWithSource = (source: EventPageSource, event: EventVO | undefined = undefined, isUserFirstEvent = false): string => {
  const baseURI = event ? getBaseEventPageURL(event) : 'e';
  const url = new URL(window.PMW.util.site_url(baseURI));

  if (areQueryParamsNotNeededForEventPageSource(source)) {
    return url.toString();
  }
  url.searchParams.append(EVENT_PAGE_SOURCE_URL_PARAM, source.toString());
  url.searchParams.append(EVENT_PAGE_CONFETTI_URL_PARAM, isUserFirstEvent ? '1' : '0');
  return url.toString();
};

export const getBaseEventPageURL = (event: EventVO): string => {
  const baseURL = 'e';

  const venue = getVenueSEOForEventPageURL(event.venue);
  const eventTypeName = event.type.name;

  const seoURI = cleanURIComponent(`${venue ? venue : ''}${eventTypeName ? `-${eventTypeName}` : ''}-${event.title}`);
  const siteStr = `${baseURL}/${seoURI}/${event.hashedID}`;

  return siteStr.toLowerCase();
};

const getVenueSEOForEventPageURL = (placeDetails: PlaceDetails | undefined): string => {
  let venue = 'online';

  if (placeDetails) {
    const city = placeDetails.city;
    const country = placeDetails.country;

    if (city && city.trim() !== '' && country && country.trim() !== '') {
      venue = `${city}-${country}`;
    } else if (city) {
      venue = city;
    } else if (country) {
      venue = country;
    }
  }
  return venue;
};

export const getEventEditURL = (event: EventVO): string => {
  return window.PMW.util.site_url(`event/wizard/${event.hashedID}`);
};

export const openEventInEditor = (event: EventVO): void => {
  redirectUser(getEventEditURL(event), false, false);
};

export const getEventSignupFormEditUrl = (signupFormID: string): string => {
  return window.PMW.util.site_url(`formmaker/load/${signupFormID}`);
};

export const isCurrentUserEventCreator = (event: EventVO): boolean => {
  const currentUserId = getUserId();

  if (!currentUserId) {
    return false;
  }

  return isUserEventCreator(event, currentUserId);
};

export const isUserEventCreator = (event: EventVO, userId: number): boolean => {
  return event.creatorId === userId;
};

export const isEventShared = (event: EventVO): boolean => {
  return !isCurrentUserEventCreator(event);
};

export const isEventSharedExternally = (event: EventVO): boolean => {
  return !!event.eventAccessInfo?.isExternallyShared;
};

export const doesUserHaveReadOnlyAccessForEvent = (event: EventVO): boolean => {
  return isEventSharedExternally(event);
};

export const canUserPerformWriteActionToEvent = (event: EventVO): boolean => {
  return isCurrentUserEventCreator(event) || !isEventSharedExternally(event);
};

export const isUserEventOwnerOrTeamAdmin = (event: EventVO): boolean => {
  return isCurrentUserEventCreator(event) || isUserEventTeamAdmin(event);
};

export const isUserEventTeamAdmin = (event: EventVO): boolean => {
  return !!event.eventAccessInfo?.isUserEventTeamAdmin;
};

export const getEventsLazyDataBasedOnAppliedFilters = (state: EventsReduxState, _eventsViewType: EventsViewType, eventFilters: EventsFilters): EventsListLazyData => {
  if (areEventFiltersInDefaultState(eventFilters)) {
    return state.eventsListDefaultLazyData;
  }
  return state.eventsListFilteredLazyData;
};

export const getFilteredEvents = (events: EventVO[], filters: EventsFilters): EventVO[] => {
  return events.filter((userEvent) => {
    return (
      isEventIncludedInSearch(userEvent, filters.searchTerm) &&
      isEventIncludedInStatusFilters(userEvent, filters.statusFilter) &&
      isEventIncludedInShareEventFilter(userEvent, filters.sharedEventsActiveFilter)
    );
  });
};

/**
 * Mirrors the SQL in `EventMyStuffFilters::applyStatusFilters`: the four ticked boxes are OR'd and each names
 * a disjoint set, so every event is judged by exactly one of them. A draft answers to the Drafts box alone,
 * whatever its dates; a published event answers to its time bucket. Leave Drafts unticked and drafts are gone.
 */
const isEventIncludedInStatusFilters = (userEvent: EventVO, statusFilters: EventStatusFilterMap): boolean => {
  if (isDraftEvent(userEvent)) {
    return statusFilters[EventStatusFilter.DRAFT];
  }

  return statusFilters[getEventTimeBasedStatus(userEvent)];
};

export const filterEventsBySearch = (events: EventVO[], searchTerm: string): EventVO[] => {
  return events.filter((userEvent) => {
    return isEventIncludedInSearch(userEvent, searchTerm);
  });
};

export const sortEvents = (events: EventVO[], sortEventsBy: EventSortBy): EventVO[] => {
  if (sortEventsBy === EventSortBy.EVENT_START_DATE) {
    return events.sort((event1, event2) => {
      return sortEventsByDates(event1.time?.date_start ?? '', event2.time?.date_start ?? '');
    });
  }

  return events.sort((event1, event2) => {
    return sortEventsByDates(event1.lastModified, event2.lastModified);
  });
};

export const filterEvents = (events: EventVO[], filters: EventsFilters): EventVO[] => {
  return sortEvents(getFilteredEvents(events, filters), filters.sortType);
};

export const openUserEventPreviewDialog = (event: EventVO, additionalProps: Omit<EventsIframeModalProps, 'event' | 'iframeTitle' | 'iframeURL'> = {}): void => {
  openEventPreviewModal({
    iframeTitle: event.title,
    iframeURL: getEventPreviewIframeURL(event),
    event,
    ...additionalProps,
  });
};

/**
 * The preview dialog is the one place a draft's page is allowed to render, so it is the one place that adds
 * {@link EVENT_DRAFT_PREVIEW_URL_PARAM}. A published event needs nothing extra and keeps its plain URL, which
 * is also the URL its Open and Copy link actions hand out.
 */
const getEventPreviewIframeURL = (event: EventVO): string => {
  const url = getEventPageURL(event);

  if (!isDraftEvent(event)) {
    return url;
  }

  return `${url}?${EVENT_DRAFT_PREVIEW_URL_PARAM}=1`;
};

export const openEventVenueExternally = (event: EventVO): void => {
  if (event.venue?.googleMapUrl) {
    window.open(event.venue.googleMapUrl, '_blank');
    return;
  }

  openOnlineEventLink(event);
};

export const openOnlineEventLink = (event: EventVO): void => {
  const onlineEventLink = event.venueOnline?.link;
  if (!onlineEventLink || isPastEvent(event)) {
    return;
  }

  if (!isSafeWebUrl(onlineEventLink)) {
    return;
  }

  window.open(onlineEventLink, '_blank');
};

export const getUTCEventStartDate = (event: EventVO): Date => {
  return getDateFromUnixTimestamp(event.startDateTimestamp);
};

export const getUTCEventEndDate = (event: EventVO): Date => {
  return getDateFromUnixTimestamp(event.endDateTimestamp);
};

/**
 * event startDateTime is of format: 2025-03-24 07:00:00 this is in UTC time, we convert it to timezone time using timezone offset in format +02:00
 * @param event
 */
export const getTimezoneAdjustedEventStartDate = (event: EventVO): Date => {
  return convertUTCTimeToGivenTimezone(new Date(event.startDateTime), event.timezoneOffset ?? '+0:00');
};

export const getTimezoneAdjustedEventEndDate = (event: EventVO): Date => {
  return convertUTCTimeToGivenTimezone(new Date(event.endDateTime), event.timezoneOffset ?? '+0:00');
};

export const isOngoingEvent = (event: EventVO): boolean => {
  const eventStartDate = getUTCEventStartDate(event);
  const eventEndDate = getUTCEventEndDate(event);
  const currentDate = new Date();
  return isBetweenDates(currentDate, eventStartDate, eventEndDate);
};

export const isPastEvent = (event: EventVO): boolean => {
  return hasDatedPassed(getUTCEventEndDate(event));
};

export const isDraftEvent = (event: EventVO): boolean => {
  return event.isDraft;
};

/**
 * The My Stuff row's "this event is over" state: no status pill, and a greyed thumbnail and stats (T4.17).
 * It is the same set of rows the server's PAST status box matches (`state = PUBLISHED AND date_end < now`),
 * so a draft is never past here however its date reads.
 */
export const isPastPublishedEvent = (event: EventVO): boolean => {
  return !isDraftEvent(event) && isPastEvent(event);
};

/**
 * A My Stuff row's text colour: the disabled grey once the event is past and published, otherwise the
 * colour the element normally has.
 */
export const getEventRowTextColorClass = (event: EventVO, activeColorClass: string): string => {
  return isPastPublishedEvent(event) ? 'ct-neutral-primary-disabled' : activeColorClass;
};

/**
 * The server already returns past published events last (`EventMyStuffFilters::applyPastEventsLastOrder`), so
 * this is normally a no-op. It covers an event that ended while the page was open: the server grouped it as not
 * past, and without this it would render greyed above the "Past events" separator. Stable, so each group keeps
 * the server's order.
 */
export const getEventsWithPastPublishedLast = (events: EventVO[]): EventVO[] => {
  return [...events.filter((event) => !isPastPublishedEvent(event)), ...events.filter(isPastPublishedEvent)];
};

export const hasEventVenue = (event: EventVO): boolean => {
  return Boolean(event.venue) || Boolean(event.venueOnline?.link);
};

export const isUpcomingEvent = (event: EventVO): boolean => {
  return isFutureDate(getUTCEventStartDate(event));
};

export const isRecurringEvent = (event: EventVO): boolean => {
  return !!event.recurring;
};

export const shouldShowRecurringEventRelatedInfo = (event: EventVO): boolean => {
  return isRecurringEvent(event) && !!event.doShowRecurringEventInfo;
};

export const isDailyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.DAILY;
};

export const isMonthlyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.MONTHLY && !!recurringEventPattern.recurringMonthly;
};

export const isSingleDayWeeklyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length === 1;
};

export const isMultipleDayWeeklyRecurringEvent = (recurringEventPattern?: RecurringEvent): boolean => {
  return recurringEventPattern?.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length > 1;
};

export const getWeekdayForSingleDayWeeklyRecurringEvent = (recurringEventPattern: RecurringEvent): string => {
  return getTranslatedWeekdays()[ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0])];
};

export const getPluralWeekdayForSingleDayWeeklyRecurringEvent = (recurringEventPattern: RecurringEvent): string => {
  return getTranslatedPluralWeekdaysForEvents()[ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0])];
};

export const getMonthlyRecurringEventWeekdayInfoString = (recurringEventPattern: RecurringEvent): string => {
  return `${recurringEventPattern.recurringMonthly.weekdayNum} ${getExtraShortWeekdayString(recurringEventPattern.recurringMonthly.weekday)}`;
};

export const doesEventSpanMultipleDays = (event: EventVO): boolean => {
  const eventStartDate = getTimezoneAdjustedEventStartDate(event);
  const eventEndDate = getTimezoneAdjustedEventEndDate(event);
  return !areDatesEqual(eventStartDate, eventEndDate);
};

export const getMultiDayFormattedEventDateText = (eventStartDate: Date, eventEndDate: Date): string => {
  return `${eventStartDate.getDate()} ${getReadableShortMonth(eventStartDate)} - ${eventEndDate.getDate()} ${getReadableShortMonth(eventEndDate)}`;
};

export const loadFontFamilyInIframe = (iframeDocument: Document, fontFamily: string): void => {
  const elements = iframeDocument.querySelectorAll('.js-custom-font-family-text');

  elements.forEach((el) => {
    el.classList.add('-loading');
  });

  appendFontStyleSheetToDOM(fontFamily, getFontStyleSheetPath(fontFamily), iframeDocument, () => {
    iframeDocument.fonts.ready
      .then(() => {
        loadAndApplyFontFamilyInIframe(iframeDocument, fontFamily, elements);
      })
      .catch(noop);
  });
};

export const loadAndApplyFontFamilyInIframe = (iframeDocument: Document, fontFamily: string, elementsToApplyFontOn: NodeListOf<Element>): void => {
  void iframeDocument.fonts.load(`16px '${fontFamily}'`).then(() => {
    // Apply the font family and remove the '-loading' class for all matching elements
    elementsToApplyFontOn.forEach((el) => {
      (el as HTMLHeadingElement | HTMLSpanElement).style.setProperty('font-family', getFontFamilyStyleString(fontFamily), 'important');
      el.classList.remove('-loading');
    });
  });
};

export const getDefaultLazyData = (loadingState: LoadingStates = LoadingStates.NOT_LOADED): EventsListLazyData => {
  return {
    ids: [],
    loadingState,
    loadMore: true,
  };
};

export const areEventFiltersInDefaultState = (filters: EventsFilters): boolean => {
  return (
    filters.sortType === EventSortBy.EVENT_START_DATE &&
    areAllEventStatusesSelectedInFilter(filters.statusFilter) &&
    isAllSharedEventsFilterSelected(filters.sharedEventsActiveFilter)
  );
};

/**
 * The Events page serves both the My events and Shared with me tabs off one view type, so Events_Create_Click reports
 * which of the two a create button was pressed on. The Events calendar tab is its own component and reports
 * MY_STUFF_EVENTS_CALENDAR itself.
 */
export const getEventsCreateGA4Source = (eventsViewType: EventsViewType): EventsCreateGA4Source => {
  switch (eventsViewType) {
    case EventsViewType.DEFAULT:
      return EventsCreateGA4Source.MY_STUFF_MY_EVENTS;
    case EventsViewType.SHARED_WITH_ME:
      return EventsCreateGA4Source.MY_STUFF_SHARED_WITH_ME;
    default:
      return assertNever(eventsViewType);
  }
};

export const isMyEventsViewType = (eventsViewType: EventsViewType): boolean => {
  return eventsViewType === EventsViewType.DEFAULT;
};

export const isSharedWithMeEventsViewType = (eventsViewType: EventsViewType): boolean => {
  return eventsViewType === EventsViewType.SHARED_WITH_ME;
};

export const isAllSharedEventsFilterSelected = (eventSharedFilterValue: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilterValue === EventsSharedWithMeFilter.ALL;
};

export const isCoPromotersSharedFilterSelected = (eventSharedFilterValue: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilterValue === EventsSharedWithMeFilter.COPROMOTERS;
};

export const isTeamValueSelectedInEventsSharedFilter = (eventSharedFilter: EventsSharedWithMeFilter): boolean => {
  return eventSharedFilter === EventsSharedWithMeFilter.TEAM;
};

export const areAllEventStatusesSelectedInFilter = (statusFilter: EventStatusFilterMap): boolean => {
  return (
    Object.values(statusFilter).find((val) => {
      return !val;
    }) === undefined
  );
};

/**
 * Returns like 'Mo, Tu, We'
 * @param recurringPattern
 */
export const getExtraShortWeekdaysString = (recurringPattern?: RecurringEvent): string | null => {
  if (!recurringPattern || recurringPattern.recurringType !== RecurringType.WEEKLY) {
    return null;
  }

  const weekdaysArraySorted = [...recurringPattern.recurringWeeks];
  weekdaysArraySorted.sort((a, b) => {
    return ALL_WEEKDAYS.indexOf(a) - ALL_WEEKDAYS.indexOf(b);
  });

  const extraShortWeekdays = [];

  for (const weekday of weekdaysArraySorted) {
    extraShortWeekdays.push(getExtraShortWeekdayString(weekday));
  }

  return weekdaysArraySorted.map((weekday) => getExtraShortWeekdayString(weekday)).join(', ');
};

export const getExtraShortWeekdayString = (weekday: Weekdays): string => {
  if (weekday === Weekdays.MONDAY) {
    return window.i18next.t('pmwjs_calendar_mo');
  }

  if (weekday === Weekdays.TUESDAY) {
    return window.i18next.t('pmwjs_calendar_tu');
  }

  if (weekday === Weekdays.WEDNESDAY) {
    return window.i18next.t('pmwjs_calendar_we');
  }

  if (weekday === Weekdays.THURSDAY) {
    return window.i18next.t('pmwjs_calendar_th');
  }

  if (weekday === Weekdays.FRIDAY) {
    return window.i18next.t('pmwjs_calendar_fr');
  }

  if (weekday === Weekdays.SATURDAY) {
    return window.i18next.t('pmwjs_calendar_sa');
  }

  if (weekday === Weekdays.SUNDAY) {
    return window.i18next.t('pmwjs_calendar_su');
  }
  return '';
};

export const getRecurringEventDateText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (recurringEventPattern.recurringType === RecurringType.DAILY) {
    return window.i18next.t('pmwjs_daily');
  }

  if (recurringEventPattern.recurringType === RecurringType.MONTHLY && recurringEventPattern.recurringMonthly?.weekday) {
    const weekdayInd = ALL_WEEKDAYS.indexOf(recurringEventPattern?.recurringMonthly?.weekday);
    return window.i18next.t('pmwjs_monthly_x_weekday', {number: recurringEventPattern.recurringMonthly.weekdayNum, weekday: getTranslatedWeekdays()[weekdayInd]});
  }

  if (recurringEventPattern.recurringType === RecurringType.WEEKLY && recurringEventPattern.recurringWeeks?.length > 0) {
    if (recurringEventPattern.recurringWeeks.length > 1) {
      const extraShortWeekdaysString = getExtraShortWeekdaysString(recurringEventPattern);
      return window.i18next.t('pmwjs_every_weekdays', {weekdays: extraShortWeekdaysString});
    }
    const weekdayInd = ALL_WEEKDAYS.indexOf(recurringEventPattern.recurringWeeks[0]);
    return window.i18next.t('pmwjs_every_weekday', {weekday: getTranslatedWeekdays()[weekdayInd]});
  }
  return '';
};

/**
 * Base text: Daily, Weekly, Monthly, any text that describes the recurring event without the specific details of the time
 */
export const getRecurringEventDateBaseText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (isDailyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_daily');
  }

  if (isMonthlyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_monthly');
  }

  if (isSingleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return getPluralWeekdayForSingleDayWeeklyRecurringEvent(recurringEventPattern);
  }

  if (isMultipleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return window.i18next.t('pmwjs_weekly');
  }

  return '';
};

/**
 * constructs the descriptive part of a recuring events' date.
 * for example: Tu, We, Th , or: 1st Mo
 * @param recurringEventPattern
 */
export const getRecurringEventDateDescriptiveText = (recurringEventPattern?: RecurringEvent): string => {
  if (!recurringEventPattern) {
    return '';
  }

  if (isMonthlyRecurringEvent(recurringEventPattern)) {
    return getMonthlyRecurringEventWeekdayInfoString(recurringEventPattern);
  }

  if (isMultipleDayWeeklyRecurringEvent(recurringEventPattern)) {
    return getExtraShortWeekdaysString(recurringEventPattern) ?? '';
  }

  return '';
};

/**
 * constructs the full text for example: Weekly · Tu, We, Th
 */
export const getRecurringEventTextWithDetails = (recurringEventPattern?: RecurringEvent): string => {
  const baseText = getRecurringEventDateBaseText(recurringEventPattern);
  const descriptiveText = getRecurringEventDateDescriptiveText(recurringEventPattern);

  if (!descriptiveText) {
    return baseText;
  }

  return `${baseText} · ${descriptiveText}`;
};

export const doDeleteEvent = async (eventHashedId: EventHashedID, onDeleteSuccess: VoidFunction, deleteAllRecurringEvents = false): Promise<void> => {
  showLoading('deletingEvent', {
    text: window.i18next.t('pmwjs_deleting'),
  });

  try {
    await window.PMW.writeLocal('event/deleteEvent', {
      event_hid: eventHashedId,
      deleteAllRecurringEvents,
    });
  } catch (err) {
    onAjaxError(err);
    return;
  }

  onDeleteSuccess();
  hideLoading('deletingEvent');
};

export const startEventRemovalFlow = (panelProps: DeleteEventConfirmationPanelProps): void => {
  openDeleteEventConfirmationModal(panelProps);
};

export const getDummyEventItem = (): EventVO => {
  return {
    hashedID: getUniqueString(),
    title: '',
    description: '',
    plainTextDescription: '',
    endDateTime: '',
    startDateTime: '',
    startDateTimestamp: getUnixTimestamp(new Date()),
    endDateTimestamp: getUnixTimestamp(new Date()),
    numRegistrations: 0,
    showCountdown: false,
    isRemoveWatermark: false,
    isShared: false,
    isPast: false,
    tzcode: '',
    registrationFields: {
      areRegistrationsRequired: true,
      email: false,
      name: false,
      phone: false,
      registerBtnText: '',
    },
    organiser: {
      name: '',
      email: '',
      profilePic: '',
      isProfilePicturePrefilled: false,
      socialMediaLinks: initSocialMediaProfileLinks(),
    },
    status: EventStatus.PUBLISHED,
    isDraft: false,
    isPublished: true,
    type: {
      hashedID: '',
      name: '',
    },
    lastModified: '',
    style: {
      fontFamily: '',
      colors: {
        primary: '',
        light: '',
        lighter: '',
        dark: '',
        darker: '',
      },
    },
    allowRegistrations: false,
    creatorId: -1,
    timezoneOffsetWithAbbreviation: 'GMT+5',
    timezoneOffset: '+5',
    numViews: 0,
  };
};

export const getDummyCalendarMetaObject = (): EventCalendarMetaData => {
  return {
    calendarName: '',
    calendarHID: '',
    topXEventImages: [],
    numLiveFutureEvents: 0,
    numUpcomingEventsThisWeek: 0,
    numSocialConnectedAccounts: 0,
    singleSocialPlatformName: null,
    numMailingLists: 0,
    hasSenderProfile: false,
  };
};

/**
 * Whether the My Stuff row shows a status pill at all. Per Figma 4528-14100 a past *published* event
 * carries none — its whole row is greyed instead. A draft keeps its pill however its date reads, which
 * is why this is not simply `!isPastEvent`.
 *
 * Two kinds of reader, and both are needed. `EventStatus` consults it to decide whether to render at all
 * (T2.18 moved the rule inside the component so a third call site inherits it). Callers consult it for the
 * *layout* that surrounds a pill which may not appear — the date column dedupes its inline "Live" text
 * against it, and `EventItemInfo` gates the separator dots either side of the small pill on it, because a
 * separator drawn around a null pill reads as `date · · guests` (T4.10).
 */
export const hasEventStatusPill = (event: EventVO): boolean => {
  return !isPastPublishedEvent(event);
};

export const getSortedEventsBasedOnLiveStatus = (eventsList: EventVO[]): EventVO[] => {
  return eventsList.sort((event1, event2) => {
    const isEvent1Ongoing = isOngoingEvent(event1);
    const isEvent2Ongoing = isOngoingEvent(event2);
    if (isEvent1Ongoing && !isEvent2Ongoing) {
      return -1;
    }

    if (isEvent2Ongoing && !isEvent1Ongoing) {
      return 1;
    }

    return 0;
  });
};

export const CUSTOM_EVENT_GUEST_INFO_PHONE_TOGGLED = 'guest_info_phone_toggle_wizard';

/**
 * Custom button-text presets shown in the wizard's step-2 CTA picker, per registration mode. The FIRST
 * entry is that mode's default — the picker highlights options[0] when the organiser hasn't picked or
 * typed a label. RSVP gets a signup-oriented set led by "Register"; Tickets and Custom link share the
 * ticket-oriented set led by "Buy tickets". Labels are translation calls so they localize.
 */
export const getButtonTextOptionsForMode = (mode: RegistrationMode): string[] => {
  if (mode === RegistrationMode.RSVP) {
    return [
      window.i18next.t('pmwjs_register'),
      window.i18next.t('pmwjs_rsvp'),
      window.i18next.t('pmwjs_claim_spot'),
      window.i18next.t('pmwjs_join'),
      window.i18next.t('pmwjs_sign_up'),
    ];
  }
  return [
    window.i18next.t('pmwjs_event_ticketing_button_buy_tickets'),
    window.i18next.t('pmwjs_event_ticketing_button_get_tickets'),
    window.i18next.t('pmwjs_register'),
    window.i18next.t('pmwjs_claim_spot'),
    window.i18next.t('pmwjs_sign_up'),
    window.i18next.t('pmwjs_join'),
  ];
};

/**
 * Merges a mode's preset button-text options with the organiser's previously-saved custom labels.
 * Order: presets first, then unique customs; duplicates (against a preset or within customs) are dropped.
 */
export const getButtonTextOptions = (presets: string[], customTexts: string[] = []): string[] => {
  return [...new Set([...presets, ...customTexts])];
};

export const shouldUpsellUserForNumRegistrations = (): boolean => {
  if (!isUserLoggedIn()) {
    return false;
  }
  return isPAYGUser();
};

export const shouldUpsellUserForRegistrationOptions = (): boolean => {
  return isPAYGUser();
};

export const openEventsUpsellDialog = (upsellType: EventUpsellType, {onDialogLoaded = noop, entryPoint}: PremiumOnlyFeatureDialogOptions = {}): void => {
  switch (upsellType) {
    case EventUpsellType.MORE_EVENTS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.MORE_EVENTS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_MORE_EVENTS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.MORE_EVENTS_PREMIUM:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.MORE_EVENTS_PREMIUM,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_NAME_MORE_EVENTS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.REMOVE_WATERMARK:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REMOVE_WATERMARK,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_EVENTS_REMOVE_WATERMARK, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.DESCRIPTION_REWRITE_AI:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.DESCRIPTION_REWRITE_AI,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_AI_WRITER, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.REGISTRATION_OPTIONS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REGISTRATION_OPTIONS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_GUESTS_INFORMATION, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.RECURRING:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.RECURRING,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_PLUS_ONLY_FEATURE_NAME_RECURRING_EVENTS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.NUM_GUESTS_LIMIT:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.NUM_GUESTS_LIMIT,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_LIMITED_REGISTRATONS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.VIEW_ANALYTICS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.VIEW_ANALYTICS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_VIEW_ANALYTICS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.REMINDERS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.REMINDERS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENTS_REMINDERS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.TICKETING:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: isPAYGUser() ? EventUpsellGA4Source.TICKETING_PAYG : EventUpsellGA4Source.TICKETING,
      });

      showLoading(TICKETING_UPSELL_LOADING_KEY);
      void window.PMW.showPremiumOnlyFeatureDialog(PREMIUM_ONLY_FEATURE_NAME_EVENT_TICKETING, {
        onDialogLoaded: (): void => {
          hideLoading(TICKETING_UPSELL_LOADING_KEY);
          onDialogLoaded();
        },
        entryPoint,
      });
      return;

    case EventUpsellType.CUSTOM_FORMS:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.CUSTOM_FORMS,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(PREMIUM_ONLY_FEATURE_NAME_EVENT_CUSTOM_FORMS, {onDialogLoaded, entryPoint});
      return;

    case EventUpsellType.SHARE_TEAM:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.SHARE_TEAM,
      });

      openTeamsUpsellDialog(TeamsUpsellSource.EVENTS_UPSELL, {onDialogLoaded});
      return;

    case EventUpsellType.EVENT_ONE_CLICK_PROMOTION:
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_UPSELLING_DIALOG, {
        [GA4EventParamName.SOURCE]: EventUpsellGA4Source.EVENT_ONE_CLICK_PROMOTION,
      });

      void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_EVENT_ONE_CLICK_PROMOTION, {onDialogLoaded, entryPoint});
      return;
  }
};

export const openUpsellDialogForEventsLimitReached = (onDialogLoaded = noop): void => {
  openEventsUpsellDialog(isPAYGUser() ? EventUpsellType.MORE_EVENTS : EventUpsellType.MORE_EVENTS_PREMIUM, {onDialogLoaded});
};

export const startCreateSocialPostFromEventFlow = (eventHashedID: EventHashedID): void => {
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_SOCIAL);
  window.location.href = window.PMW.util.site_url(`socialmedia/post/event/${eventHashedID}`);
};

export const startCreateEmailCampaignFromEventFlow = (eventHashedID: EventHashedID): void => {
  if (isPAYGUser()) {
    openUpsellingDialogForEmailCampaigns(UpsellDialogEntryPoint.EVENT_PROMOTION);
    return;
  }

  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_PROMOTE_EMAIL);
  window.PMW.redirectUrlCache.setCache(
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_CACHE_KEY,
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_REDIRECT_URI + `/${eventHashedID}`,
    window.PMW.redirectUrlCache.EVENT_EMAIL_PROMOTION_PAGE_DEFAULT_CACHE_TIMEOUT_MILLISECONDS
  );

  window.open(window.PMW.util.site_url(`emailmarketing/publish/event/${eventHashedID}`), '_self');
};

export const getTimeZoneFromTzCode = (tzcode: string): TimeZone => {
  return findTimeZoneFromTzCode(tzcode) ?? getUserDefaultTimeZone();
};

export const getUserDefaultTimeZone = (): TimeZone => {
  const userTimeZone = findTimeZoneFromTzCode(Intl.DateTimeFormat().resolvedOptions().timeZone);

  if (userTimeZone) {
    return userTimeZone;
  }
  const userUTCOffsetMins = -1 * new Date().getTimezoneOffset();

  const findApproximateUserTimeZoneFromUTC = (utcOffsetMins: number): TimeZone | undefined => {
    return timezones.find((tz: TimeZone) => {
      return getGMTOffsetMinutesFromTimeZone(tz) === utcOffsetMins;
    });
  };

  return findApproximateUserTimeZoneFromUTC(userUTCOffsetMins) ?? timezones[0];
};

export const findTimeZoneFromTzCode = (userTzCode: string): TimeZone | undefined => {
  return timezones.find((tz: TimeZone) => {
    return tz.tzCode === userTzCode;
  });
};

export const getEventPagePreviewURL = (eventHashedId: EventHashedID, previewType: EventPagePreviewType): string => {
  return repoURL(`eventpagepreviews/${eventHashedId}-${previewType}.jpg?ts=${getUnixTimestamp(new Date())}`, getWriteBucket());
};

export const getGalleryUriFromEventAiSearchParams = (aiSearchParams: EventTypeAiSearchParams | undefined): string => {
  if (aiSearchParams === undefined) {
    return GalleryThemeURI.EVENTS;
  }
  const {eventName, eventTypeHashedId} = aiSearchParams,
    eventPosterTemplatesParams = new URLSearchParams();
  eventPosterTemplatesParams.set('event_name', eventName);
  eventPosterTemplatesParams.set('event_type_hashed_id', eventTypeHashedId);
  return `posters/searchEventPosterTemplates?${eventPosterTemplatesParams.toString()}`;
};

export const hasUserReachedPAYGLimitForEventRegistrations = (event: EventVO): boolean => {
  return isPAYGUser() && event.numRegistrations >= EVENT_REGISTRATIONS_LIMIT_PAYG;
};

/**
 * Which step-2 tile an event should open on. A ticketing config that was explicitly selected answers it
 * outright; everything else is an event saved before ticketing existed, and is mapped by what the legacy
 * registration fields say: registrations off ⇒ None, registrations on with a link ⇒ Custom link,
 * registrations on without one ⇒ RSVP (the legacy signup form's closest equivalent).
 *
 * `registerBtnLink` must be tested for truthiness, not against `undefined`. `EventRegistrationFieldVO::jsonSerialize()`
 * always emits the key and `getRegisterBtnLink()` returns PHP `null` when no link is set, so the wire value for the
 * overwhelming majority of legacy events is `null` — which is neither `undefined` nor `''`, and used to fall through
 * to Custom link and leave the organiser stuck on step 2 behind an empty, required link field.
 */
export const deriveSelectedRegistrationMode = (isTicketingSelected: boolean, isRsvp: boolean, areRegistrationsRequired: boolean, registerBtnLink: string | null | undefined): RegistrationMode => {
  if (isTicketingSelected) {
    return isRsvp ? RegistrationMode.RSVP : RegistrationMode.TICKETS;
  }
  if (!areRegistrationsRequired) {
    return RegistrationMode.NONE;
  }
  if (registerBtnLink) {
    return RegistrationMode.CUSTOM_LINK;
  }
  return RegistrationMode.RSVP;
};

export const createNewPendingInvitesFromEmails = (emails: string[]): EventPendingEmailInvitation[] => {
  return emails.map((email): EventPendingEmailInvitation => {
    return {email, isExpired: false, profilePictureURL: window.PMW.util.asset_url('images/default_profile_picture_small.png')};
  });
};

export const getGA4EventForSkippedPromotionType = (promotionType: PromotionType): GA4EventName | string => {
  switch (promotionType) {
    case PromotionType.EMAIL_CAMPAIGNS:
      return GA4EventName.EVENTS_PROMOTE_EMAIL_SKIP;

    case PromotionType.EMBED_IN_WEBPAGE:
      return GA4EventName.EVENTS_PROMOTE_EMBED_SKIP;

    case PromotionType.PRINT_FLYERS:
      return GA4EventName.EVENTS_PROMOTE_PRINT_SKIP;

    case PromotionType.SHARE_EVENT_PAGE_LINK:
      return GA4EventName.EVENTS_PROMOTE_PAGE_SHARE_SKIP;

    case PromotionType.SOCIAL_POSTS:
      return GA4EventName.EVENTS_PROMOTE_SOCIAL_SKIP;

    default:
      return '';
  }
};

const sortEventsByDates = (date1: string, date2: string): number => {
  return new Date(date2).getTime() - new Date(date1).getTime();
};

const isEventIncludedInSearch = (userEvent: EventVO, searchTerm: string): boolean => {
  return userEvent.title.toLowerCase().includes(searchTerm.toLowerCase());
};

export const isEventIncludedInShareEventFilter = (event: EventVO, eventSharedFilter: EventsSharedWithMeFilter): boolean => {
  if (isAllSharedEventsFilterSelected(eventSharedFilter)) {
    return true;
  }

  if (isCoPromotersSharedFilterSelected(eventSharedFilter)) {
    return isEventSharedExternally(event);
  }

  if (isTeamValueSelectedInEventsSharedFilter(eventSharedFilter)) {
    return event.isShared;
  }

  return true;
};

/**
 *
 */
const getEventTimeBasedStatus = (userEvent: EventVO): EventStatusFilter => {
  if (isOngoingEvent(userEvent)) {
    return EventStatusFilter.LIVE;
  }

  if (isUpcomingEvent(userEvent)) {
    return EventStatusFilter.UPCOMING;
  }

  if (isPastEvent(userEvent)) {
    return EventStatusFilter.PAST;
  }

  return EventStatusFilter.PAST;
};

const areQueryParamsNotNeededForEventPageSource = (prefillSource: EventPageSource): boolean => {
  return prefillSource === EventPageSource.DEFAULT;
};

export const getUserEvent = (eventHashedId: string): Promise<EventVO> => {
  return window.PMW.readLocal('event/getEvent', {
    event_hid: eventHashedId,
  });
};

export const initEventPromoteOptionBorderAnimationSeenState = (): void => {
  if (typeof window.PMW.hasSeenPromoteOptionAnimationInCurrentSession === 'undefined') {
    window.PMW.hasSeenPromoteOptionAnimationInCurrentSession = false;
  }
};

export const markEventPromoteOptionBorderAnimationAsSeen = (): void => {
  initEventPromoteOptionBorderAnimationSeenState();
  window.PMW.hasSeenPromoteOptionAnimationInCurrentSession = true;
};

export const shouldShowEventPromoteOptionBorderAnimation = (): boolean => {
  initEventPromoteOptionBorderAnimationSeenState();
  return !window.PMW.hasSeenPromoteOptionAnimationInCurrentSession;
};

export const getSecondaryColorForEventsCalendar = (primaryColor: string): string => {
  const textColorWithPrimaryColorAsBg = getColorPaletteForContainer(primaryColor).textColor;

  if (textColorWithPrimaryColorAsBg === PMW_COLORS_NEUTRAL.NEUTRAL_0) {
    return primaryColor;
  }

  return textColorWithPrimaryColorAsBg;
};
